"use client";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { api, Guide } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { getToken } from "@/lib/auth";

export default function GuidePage() {
  const router = useRouter();
  const t = useTranslations("guide");
  const locale = useLocale();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!getToken()) { router.push("/login"); return; }
    api.getGuide(locale)
      .then(setGuide)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [router, locale]);

  const content = guide?.content;

  const sections = useMemo(() => {
    if (!content) return [];
    const q = query.trim().toLowerCase();
    if (!q) return content.sections;
    return content.sections.filter(
      (s) =>
        s.heading.toLowerCase().includes(q) ||
        s.body.toLowerCase().includes(q) ||
        s.bullets.some((b) => b.toLowerCase().includes(q))
    );
  }, [content, query]);

  const updated = guide?.generated_at
    ? new Date(guide.generated_at).toLocaleDateString(locale === "tr" ? "tr-TR" : "en-US", {
        year: "numeric", month: "long", day: "numeric",
      })
    : null;

  return (
    <AppShell active="guide">
      <main className="max-w-3xl mx-auto px-5 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <span className="text-[var(--text-muted)] text-sm">{t("loading")}</span>
          </div>
        ) : !content ? (
          <div className="flex items-center justify-center py-24">
            <span className="text-[var(--text-muted)] text-sm">{t("loading")}</span>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
                {content.title}
              </h1>
              {content.intro && (
                <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{content.intro}</p>
              )}
              {updated && (
                <p className="text-xs text-[var(--text-muted)] mt-3">
                  {t("updated")}: {updated} · {t("autoNote")}
                </p>
              )}
            </div>

            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="w-full mb-6 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:bg-[var(--surface)] transition-colors"
            />

            {sections.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-xl p-6 text-center">
                {t("noResults")}
              </p>
            ) : (
              <div className="space-y-4">
                {sections.map((s, i) => (
                  <section key={i} className="bg-white rounded-2xl border border-[var(--border)] p-6">
                    <h2 className="text-base font-semibold text-[var(--text-primary)] mb-2" style={{ fontFamily: "var(--font-lora)" }}>
                      {s.heading}
                    </h2>
                    {s.body && (
                      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{s.body}</p>
                    )}
                    {s.bullets.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {s.bullets.map((b, j) => (
                          <li key={j} className="flex gap-2.5 text-sm text-[var(--text-secondary)] leading-relaxed">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>
            )}

            {content.footer && (
              <p className="text-xs text-[var(--text-muted)] italic mt-8 leading-relaxed">{content.footer}</p>
            )}
          </>
        )}
      </main>
    </AppShell>
  );
}
